import React from 'react';
import { Calendar, Users, FileText, Home, BookOpen } from 'lucide-react';
import { motion } from 'framer-motion';
import { DS } from '../../lib/design-system';

export type TabId =
  | 'agenda'
  | 'bitacora'
  | 'reuniones'
  | 'propuestas'
  | 'gestiones'
  | 'electoral'
  | 'settings';

interface BottomNavProps {
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
}

interface NavItem {
  id: TabId;
  icon: typeof Calendar;
  label: string;
}

// Bitácora occupies the slot previously held by Electoral; Electoral stays
// reachable via the sidebar / hamburger (`MENU_ITEMS` in constants.ts).
const NAV_ITEMS: NavItem[] = [
  { id: 'agenda', icon: Calendar, label: 'Agenda' },
  { id: 'reuniones', icon: Users, label: 'Reuniones' },
  { id: 'propuestas', icon: FileText, label: 'Propuestas' },
  { id: 'gestiones', icon: Home, label: 'Gestiones' },
  { id: 'bitacora', icon: BookOpen, label: 'Bitácora' },
];

// Optimized transition - using eased curve instead of spring for better mobile performance
const navTransition = { duration: 0.2, ease: [0.4, 0, 0.2, 1] as const };

export const BottomNav: React.FC<BottomNavProps> = ({ activeTab, onTabChange }) => {
  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-[#5D1826] border-t border-[#7A2435] z-50 pb-safe" style={{ boxShadow: '0 -5px 15px rgba(0,0,0,0.03)' }}>
      <div className="flex justify-around items-center h-16">
        {NAV_ITEMS.map((item) => {
          const isActive = activeTab === item.id;
          const Icon = item.icon;

          return (
            <motion.button
              key={item.id}
              onClick={() => onTabChange(item.id)}
              className="flex flex-col items-center justify-center flex-1 h-full relative"
              whileTap={DS.animations.buttonTap}
              // Consolidated animation: y position + scale in one animate prop
              animate={{
                y: isActive ? -4 : 0,
                scale: isActive ? 1.02 : 1,
              }}
              transition={navTransition}
            >
              {/* Icon - using CSS transition for color, Framer for transform */}
              <Icon
                size={22}
                className={`transition-all duration-200 ${
                  isActive ? 'text-white scale-110' : 'text-white/60 scale-100'
                }`}
                strokeWidth={isActive ? 2.5 : 2}
              />

              {/* Label - using CSS transition for simpler opacity/transform */}
              <span
                className={`text-xs mt-1 font-medium transition-all duration-200 ${
                  isActive ? 'text-white opacity-100' : 'text-white/60 opacity-60 translate-y-0.5'
                }`}
              >
                {item.label}
              </span>

              {/* Active indicator - optimized transition */}
              {isActive && (
                <motion.div
                  layoutId="activeNavIndicator"
                  className="absolute -top-0.5 w-8 h-0.5 bg-[#B89658] rounded-full"
                  transition={navTransition}
                />
              )}
            </motion.button>
          );
        })}
      </div>
    </nav>
  );
};
